import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminApi, errorMessage } from './adminApi';
import { useQueryParams } from './useQueryParams';
import { formatDate, formatDateTime, formatRelative } from './format';
import {
  DataTable, Drawer, EmptyState, ListLayout, Pagination, Panel, PriorityBadge, SearchInput, SegmentedControl, Spinner,
  StatusBadge, Toolbar, btnDanger, btnPrimary, btnSecondary, inputClass, labelClass, selectClass
} from './AdminUI';

const PRIORITIES = ['urgent', 'high', 'medium', 'low'];
const SORTS = [
  { value: 'activity', label: 'Last activity' },
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'priority', label: 'Priority' }
];

function NewTicketForm({ condo, onDone, onCancel }) {
  const api = useAdminApi();
  const [form, setForm] = useState({ participantEmail: '', message: '' });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/api/tickets/admin/create', { ...form, condoId: condo._id });
      onDone('Ticket submitted for approval. Review it in the Approvals tab.');
    } catch (err) {
      alert(errorMessage(err, 'Failed to submit ticket for approval'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label htmlFor="new-ticket-email" className={labelClass}>Participant email *</label>
        <input id="new-ticket-email" type="email" required value={form.participantEmail} onChange={(e) => setForm({ ...form, participantEmail: e.target.value })} className={inputClass} placeholder="resident@example.com" />
        <p className="text-xs text-muted-foreground mt-1">Email of the person who reported the issue</p>
      </div>
      <div>
        <label htmlFor="new-ticket-message" className={labelClass}>Ticket information *</label>
        <textarea id="new-ticket-message" required rows={8} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputClass} placeholder="Describe the issue in detail. Include the original date if known." />
        <p className="text-xs text-muted-foreground mt-1">AI turns this into the ticket title, description and priority. It goes to Approvals first.</p>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Submitting...' : 'Submit for approval'}</button>
        <button type="button" onClick={onCancel} className={btnSecondary}>Cancel</button>
      </div>
    </form>
  );
}

function TicketUpdates({ ticket, onChanged, notify }) {
  const api = useAdminApi();
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState('');
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (action, fallback) => {
    setBusy(true);
    try {
      await action();
    } catch (err) {
      alert(errorMessage(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = (update) => run(async () => {
    await api.put(`/api/admin/tickets/${ticket._id}/updates/${update._id}`, { content: editText });
    setEditing(null);
    notify('Update edited.');
    onChanged();
  }, 'Failed to edit update');

  const remove = (update) => {
    if (!window.confirm('Delete this update?')) return;
    run(async () => {
      await api.del(`/api/admin/tickets/${ticket._id}/updates/${update._id}`);
      notify('Update deleted.');
      onChanged();
    }, 'Failed to delete update');
  };

  const add = () => run(async () => {
    await api.post(`/api/admin/tickets/${ticket._id}/updates`, { content: newText });
    setAdding(false);
    setNewText('');
    notify('Update submitted for approval. Review it in the Approvals tab.');
    onChanged();
  }, 'Failed to submit update for approval');

  const updates = ticket.updates || [];

  return (
    <section className="mt-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-foreground">Updates ({updates.length})</h3>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} className="text-primary hover:text-primary/80 text-sm">+ Add update</button>
        )}
      </div>
      {adding && (
        <div className="space-y-2 mb-3">
          <textarea value={newText} onChange={(e) => setNewText(e.target.value)} rows={3} maxLength={500} placeholder="Enter update content..." aria-label="New update" className={inputClass} />
          <p className="text-xs text-muted-foreground">AI polishes the wording, then it goes to Approvals. Followers are notified when it's approved.</p>
          <div className="flex gap-2">
            <button type="button" onClick={add} disabled={busy || !newText.trim()} className={btnPrimary}>{busy ? 'Submitting...' : 'Submit for approval'}</button>
            <button type="button" onClick={() => { setAdding(false); setNewText(''); }} className={btnSecondary}>Cancel</button>
          </div>
        </div>
      )}
      {updates.length === 0 ? (
        <p className="text-sm text-muted-foreground">No updates yet.</p>
      ) : (
        <ol className="space-y-3">
          {updates.map((update) => (
            <li key={update._id} className="border-l-2 border-border pl-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{formatDateTime(update.timestamp)}</span>
                {editing !== update._id && (
                  <span className="ml-auto flex gap-2">
                    <button type="button" onClick={() => { setEditing(update._id); setEditText(update.content); }} className="text-primary hover:text-primary/80">Edit</button>
                    <button type="button" onClick={() => remove(update)} className="text-destructive hover:text-destructive/80">Delete</button>
                  </span>
                )}
              </div>
              {editing === update._id ? (
                <div className="space-y-2 mt-1">
                  <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} aria-label="Edit update" className={inputClass} />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => saveEdit(update)} disabled={busy} className={btnPrimary}>Save update</button>
                    <button type="button" onClick={() => setEditing(null)} className={btnSecondary}>Cancel</button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-foreground whitespace-pre-wrap mt-1">{update.content}</p>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function TicketDrawer({ ticket, condo, onClose, onChanged, notify }) {
  const api = useAdminApi();
  const [form, setForm] = useState({ title: ticket.title, description: ticket.description, priority: ticket.priority, status: ticket.status });
  const [saving, setSaving] = useState(false);

  const save = async (changes, message) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/api/admin/tickets/${ticket._id}`, changes);
      notify(message);
      onChanged(data.ticket);
    } catch (err) {
      alert(errorMessage(err, 'Failed to update ticket'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ticket "${ticket.title}"? This cannot be undone.`)) return;
    try {
      await api.del(`/api/admin/tickets/${ticket._id}`);
      notify('Ticket deleted.');
      onClose();
      onChanged();
    } catch (err) {
      alert(errorMessage(err, 'Failed to delete ticket'));
    }
  };

  const isOpen = ticket.status === 'open';

  return (
    <Drawer
      title={ticket.title}
      badges={<>{ticket.ticketNumber && <span className="text-sm text-muted-foreground">{ticket.ticketNumber}</span>}<PriorityBadge priority={ticket.priority} /><StatusBadge status={ticket.status} /></>}
      subtitle={<>Opened {formatDate(ticket.createdAt)} · <Link to={`/${condo.slug}/tickets?ticket=${ticket._id}`} className="text-primary hover:underline">View as resident</Link></>}
      onClose={onClose}
    >
      <form onSubmit={(e) => { e.preventDefault(); save(form, 'Ticket saved.'); }} className="space-y-3">
        <div>
          <label htmlFor="ticket-title" className={labelClass}>Title</label>
          <input id="ticket-title" required value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label htmlFor="ticket-description" className={labelClass}>Description</label>
          <textarea id="ticket-description" required rows={4} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="ticket-priority" className={labelClass}>Priority</label>
            <select id="ticket-priority" value={form.priority || 'medium'} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={inputClass}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="ticket-status" className={labelClass}>Status</label>
            <select id="ticket-status" value={form.status || 'open'} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputClass}>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Saving...' : 'Save'}</button>
          <button
            type="button"
            disabled={saving}
            onClick={() => save({ status: isOpen ? 'closed' : 'open' }, isOpen ? 'Ticket closed.' : 'Ticket reopened. Followers are notified.')}
            className={btnSecondary}
          >
            {isOpen ? 'Close ticket' : 'Reopen ticket'}
          </button>
          <button type="button" onClick={remove} className={`${btnDanger} ml-auto`}>Delete</button>
        </div>
      </form>
      <TicketUpdates ticket={ticket} onChanged={() => onChanged()} notify={notify} />
    </Drawer>
  );
}

function TicketsTab({ condo, notify, onCountsChange }) {
  const api = useAdminApi();
  const [params, setParams] = useQueryParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [focused, setFocused] = useState(null);

  const status = params.get('status') || 'open';
  const priority = params.get('priority') || '';
  const sort = params.get('sort') || 'activity';
  const q = params.get('q') || '';
  const page = Number(params.get('page')) || 1;
  const selectedId = params.get('ticket');
  const creating = params.get('new') === '1';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/admin/condos/${condo._id}/tickets`, { status, priority: priority || undefined, sort, q: q || undefined, page });
      setData(response.data);
    } catch (err) {
      alert(errorMessage(err, 'Failed to fetch tickets'));
    } finally {
      setLoading(false);
    }
  }, [api, condo._id, status, priority, sort, q, page]);

  useEffect(() => { load(); }, [load]);

  const listed = data?.tickets.find((t) => String(t._id) === String(selectedId));
  const selected = listed || (focused && String(focused._id) === String(selectedId) ? focused : null);

  useEffect(() => {
    if (!selectedId || listed || loading) return;
    api.get(`/api/tickets/${selectedId}`)
      .then((response) => setFocused(response.data.ticket))
      .catch(() => setParams({ ticket: null }, { replace: true }));
  }, [api, selectedId, listed, loading, setParams]);

  const changed = (updatedTicket) => {
    if (updatedTicket) setFocused(updatedTicket);
    load();
    onCountsChange();
  };

  const clearAll = async () => {
    if (!window.confirm(`Delete ALL tickets in "${condo.name}"? This cannot be undone.`)) return;
    try {
      const response = await api.del(`/api/admin/condos/${condo._id}/tickets`);
      notify(response.data.message);
      changed();
    } catch (err) {
      alert(errorMessage(err, 'Failed to clear tickets'));
    }
  };

  const counts = data?.counts || {};
  const columns = [
    { key: 'ticketNumber', header: '#', className: 'whitespace-nowrap text-muted-foreground', render: (t) => t.ticketNumber || '—' },
    { key: 'title', header: 'Title', className: 'font-medium text-foreground' },
    { key: 'priority', header: 'Priority', render: (t) => <PriorityBadge priority={t.priority} /> },
    { key: 'status', header: 'Status', hideBelow: 'md', render: (t) => <StatusBadge status={t.status} /> },
    { key: 'createdAt', header: 'Opened', hideBelow: 'xl', className: 'whitespace-nowrap text-muted-foreground', render: (t) => formatDate(t.createdAt) },
    { key: 'lastActivityAt', header: 'Last activity', hideBelow: 'md', className: 'whitespace-nowrap text-muted-foreground', render: (t) => formatRelative(t.lastActivityAt) },
    { key: 'updates', header: 'Updates', hideBelow: 'xl', render: (t) => (t.updates || []).length }
  ];

  const drawer = creating ? (
    <Drawer title="New ticket" onClose={() => setParams({ new: null })}>
      <NewTicketForm
        condo={condo}
        onCancel={() => setParams({ new: null })}
        onDone={(message) => { notify(message); setParams({ new: null }); onCountsChange(); }}
      />
    </Drawer>
  ) : selected ? (
    <TicketDrawer key={`${selected._id}-${selected.updatedAt}-${selected.status}`} ticket={selected} condo={condo} notify={notify} onChanged={changed} onClose={() => setParams({ ticket: null })} />
  ) : null;

  return (
    <ListLayout drawer={drawer}>
      <Panel>
        <Toolbar>
          <SegmentedControl
            label="Ticket status"
            value={status}
            onChange={(value) => setParams({ status: value === 'open' ? null : value, page: null })}
            options={[
              { value: 'open', label: 'Open', count: counts.open },
              { value: 'closed', label: 'Closed', count: counts.closed },
              { value: 'all', label: 'All', count: counts.all }
            ]}
          />
          <select aria-label="Priority" value={priority} onChange={(e) => setParams({ priority: e.target.value, page: null })} className={selectClass}>
            <option value="">Any priority</option>
            {PRIORITIES.map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
          </select>
          <select aria-label="Sort" value={sort} onChange={(e) => setParams({ sort: e.target.value === 'activity' ? null : e.target.value, page: null })} className={selectClass}>
            {SORTS.map((s) => <option key={s.value} value={s.value}>Sort: {s.label}</option>)}
          </select>
          <SearchInput value={q} onChange={(value) => setParams({ q: value, page: null }, { replace: true })} placeholder="Filter tickets by number, title or text..." />
          <button type="button" onClick={() => setParams({ new: '1', ticket: null })} className={btnPrimary}>+ New ticket</button>
        </Toolbar>
        {loading && !data ? <Spinner /> : !data ? <EmptyState>Tickets could not be loaded. Try again in a moment.</EmptyState> : (
          <>
            <DataTable
              label="Tickets"
              columns={columns}
              rows={data?.tickets || []}
              selectedId={selectedId}
              onRowClick={(t) => setParams({ ticket: t._id, new: null })}
              emptyMessage={q || priority ? 'No tickets match these filters.' : status === 'open' ? 'No open tickets.' : 'No tickets yet.'}
            />
            <Pagination page={data.page} pages={data.pages} total={data.total} limit={data.limit} onChange={(n) => setParams({ page: n === 1 ? null : n })} />
          </>
        )}
        {process.env.NODE_ENV === 'development' && counts.all > 0 && (
          <div className="pt-4 mt-4 border-t border-border">
            <button type="button" onClick={clearAll} className={btnDanger}>Clear all tickets</button>
          </div>
        )}
      </Panel>
    </ListLayout>
  );
}

export default TicketsTab;
